import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { PrimaryButton } from '../components/Button';
import { FormHeader } from '../components/Card';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function JoinFamily() {
    const navigate = useNavigate();
    const { refreshMe } = useAuth();
    const [code, setCode] = useState('');
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const previewFamily = async () => {
        setError(''); setPreview(null);
        if (!code.trim()) return setError('Ingresa un código');
        try {
            const { data } = await api.get(`/family/preview/${code.trim().toUpperCase()}`);
            setPreview(data);
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const join = async () => {
        setLoading(true);
        try {
            await api.post('/family/join-requests', { family_code: code.trim().toUpperCase() });
            alert('Tu solicitud fue enviada al admin. Te avisaremos cuando sea aprobada.');
            navigate('/setup-family', { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="join-family-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <LogoHorizontal size="md" />
                <div className="w-10" />
            </div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up">
                <FormHeader title="Unirme a una familia" subtitle="Ingresa el código de familia que te compartieron." />

                <div className="space-y-4">
                    <input
                        type="text"
                        placeholder="FAM-XXXX-XXXX"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 text-center text-lg tracking-wider font-bold text-brand-navy focus:border-brand focus:ring-2 focus:ring-brand/20"
                        data-testid="join-family-code-input"
                    />

                    {!preview && (
                        <button onClick={previewFamily} className="w-full h-12 border-2 border-brand text-brand rounded-full font-semibold hover:bg-brand/5" data-testid="join-family-preview-btn">
                            Buscar familia
                        </button>
                    )}

                    {error && <div className="text-red-500 text-sm font-medium" data-testid="join-family-error">{error}</div>}

                    {preview && (
                        <div className="bg-brand/5 border border-brand/20 rounded-2xl p-5 text-center fade-up" data-testid="join-family-preview">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                                {preview.avatar_url ? (
                                    <img src={preview.avatar_url} alt="" className="w-full h-full object-cover rounded-full" />
                                ) : (
                                    <Users className="w-8 h-8 text-brand" />
                                )}
                            </div>
                            <h3 className="text-lg font-bold text-brand-navy">{preview.name}</h3>
                            <p className="text-xs text-gray-500 mt-1">{preview.members_count} miembros</p>
                            <div className="mt-4">
                                <PrimaryButton onClick={join} loading={loading} testId="join-family-confirm-btn">Solicitar unirme a {preview.name}</PrimaryButton>
                                <p className="text-[11px] text-gray-500 text-center mt-2">El admin recibirá tu solicitud y deberá aprobarla.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
